import { useMessageFormatter } from '@react-aria/i18n';
import clsx from 'clsx';
import styles from '@danske/sapphire-css/components/modalLayout/modalLayout.module.css';
import {
  SapphireStyleProps,
  useSapphireStyleProps,
  useThemeCheck,
} from '../../utils';
import intlMessages from '../i18n';

import React, { ForwardedRef, useContext } from 'react';
import { IconButton } from '../../IconButton';
import { CloseLarge } from '@danske/sapphire-icons/react';
import { ModalLayoutContext } from './ModalLayoutContext';
import { DOMProps } from '@react-types/shared';
import { Typography } from '../../Typography';
import { GlobalDomAttributes } from '../../utils/types';

export interface ModalLayoutHeaderProps
  extends DOMProps,
    GlobalDomAttributes,
    Pick<SapphireStyleProps, 'UNSAFE_style' | 'UNSAFE_className'> {
  /**
   * Main heading of the modal.
   */
  heading: React.ReactNode;
  /**
   * Optional subheading, rendered below the heading, in a secondary-looking
   * style.
   */
  subheading?: React.ReactNode;
  /**
   * Slot for optional back button, rendered to the left of heading and subheading.
   * Useful for modals with inner navigation.
   *
   * Use {@link IconButton} to render the content.
   */
  backButton?: React.ReactNode;

  /**
   * Slot for optional action button, rendered next to the close button.
   * Useful for actions or menus on a modal.
   *
   * Use {@link IconButton} to render the content.
   */
  actionButton?: React.ReactNode;

  /**
   * Whether the close icon button should not be rendered. Useful to avoid
   * redundant actions when there is already a button in for closing/canceling
   * the modal.
   * @default false
   */
  hideCloseButton?: boolean;

  /**
   * Slot for optional extra content (such as a {@link Badge}) rendered under the heading and subheading.
   */
  extra?: React.ReactNode;

  /**
   * Slot for optional progress indicator, rendered along the bottom of the header.
   * Use {@link ProgressIndicator} to render the content.
   */
  progressIndicator?: React.ReactNode;
}

/**
 * Used in {@link ModalLayoutProps#header header} part of {@link ModalLayout}.
 * Ensures consistent padding with body and footer parts.
 */

export const ModalLayoutHeader = React.forwardRef(function ModalLayoutHeader(
  {
    heading,
    subheading,
    backButton,
    actionButton,
    hideCloseButton = false,
    extra,
    progressIndicator,
    ...otherProps
  }: ModalLayoutHeaderProps,
  forwardedRef: ForwardedRef<HTMLDivElement>
): JSX.Element {
  useThemeCheck();
  const formatMessage = useMessageFormatter(intlMessages);
  const { styleProps, filteredProps } = useSapphireStyleProps(otherProps);
  const { onClose, titleProps } = useContext(ModalLayoutContext);

  return (
    <div
      className={clsx(
        styles['sapphire-modal-layout__header'],
        styleProps.className
      )}
      style={styleProps.style}
      {...filteredProps}
      ref={forwardedRef}
    >
      <div className={styles['sapphire-modal-layout__header-content']}>
        {backButton}
        <div
          className={styles['sapphire-modal-layout__heading-container']}
          {...titleProps}
        >
          <Typography.Heading level={5}>{heading}</Typography.Heading>
          {subheading && (
            <div className={clsx(styles['sapphire-modal-layout__subheading'])}>
              {subheading}
            </div>
          )}
          {extra && (
            <div className={styles['sapphire-modal-layout__extra-container']}>
              {extra}
            </div>
          )}
        </div>
        {actionButton}
        {!hideCloseButton && (
          <IconButton
            variant="tertiary"
            aria-label={formatMessage('dismiss')}
            size="md"
            onPress={onClose}
          >
            <CloseLarge />
          </IconButton>
        )}
      </div>
      {progressIndicator && (
        <div className={styles['sapphire-modal-layout__progress-container']}>
          {progressIndicator}
        </div>
      )}
    </div>
  );
});
